import { NavLink, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi, projectsApi } from "@/api";
import type { Project } from "@/types";
import {
  LayoutDashboard,
  Users,
  LogOut,
  Mail,
  FileText,
  Phone,
  GitBranch,
  MessageSquare,
  MessagesSquare,
  Target,
  ChevronDown,
  Inbox,
  ScrollText,
  PhoneOff,
  Building2,
  Bot,
  ListChecks,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "キャンペーン" },
  { to: "/sequences", icon: GitBranch, label: "シーケンス" },
  { to: "/call-campaigns", icon: Phone, label: "架電" },
  { to: "/call-transcripts", icon: ScrollText, label: "通話記録" },
  { to: "/do-not-call", icon: PhoneOff, label: "架電NGリスト" },
  { to: "/fax-received", icon: Inbox, label: "FAX受信" },
  { to: "/contacts", icon: Users, label: "コンタクト" },
  { to: "/audience-lists", icon: ListChecks, label: "宛先リスト" },
  { to: "/prospects", icon: Building2, label: "見込みリスト" },
  { to: "/templates", icon: FileText, label: "メールテンプレート" },
  { to: "/sms-templates", icon: MessageSquare, label: "SMSテンプレート" },
  { to: "/line-templates", icon: MessagesSquare, label: "LINEテンプレート" },
  { to: "/meta-audiences", icon: Target, label: "Meta広告連携" },
  { to: "/ai-manager", icon: Bot, label: "AI Manager" },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: me } = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => authApi.me(),
  });

  const { data: appConfig } = useQuery({
    queryKey: ["app-config"],
    queryFn: () => authApi.appConfig(),
  });

  const { data: projectsData } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projectsApi.list(),
  });

  const projects = projectsData?.data ?? [];
  const currentProject = projects.find((p: Project) => p.id === me?.projectId) || projects[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSwitchProject = async (project: Project) => {
    setDropdownOpen(false);
    await projectsApi.switch(project.id);
    queryClient.invalidateQueries();
  };

  const handleLogout = async () => {
    await authApi.logout().catch(() => {});
    queryClient.clear();
    navigate("/login");
  };

  return (
    <aside className="w-56 bg-slate-900 text-white min-h-screen flex flex-col">
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-blue-400" />
          <h1 className="text-lg font-bold">{appConfig?.appName ?? "MA System"}</h1>
        </div>
        <p className="text-xs text-slate-400 mt-1">{appConfig?.brandShortName ?? "Marketing Automation"}</p>
      </div>
      {projects.length > 1 && (
        <div className="px-2 pt-2" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 transition-colors"
          >
            <span className="truncate">{currentProject?.name ?? "..."}</span>
            <ChevronDown size={14} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {dropdownOpen && (
            <div className="mt-1 bg-slate-800 rounded-md overflow-hidden shadow-lg">
              {projects.map((p: Project) => (
                <button
                  key={p.id}
                  onClick={() => handleSwitchProject(p)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700 transition-colors ${
                    p.id === currentProject?.id ? "text-blue-400" : "text-slate-300"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <nav className="flex-1 p-2 space-y-1">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-slate-700 text-white"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-700 space-y-2">
        {me && (
          <p className="text-xs text-slate-400 truncate">{me.name}</p>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
        >
          <LogOut size={18} />
          ログアウト
        </button>
      </div>
    </aside>
  );
}
